import {
  WeddingDateFormFields,
  RankedDateField,
} from '~/components/common/form/WeddingDateField/types';
import { formatDateUtc } from '~/util/dateUtils';

import { formatWeddingDates, isWeddingDateInTheFuture } from './dateFunctions';

/**
 * Validates that a wedding date, from individual form selections, is not
 * booked.
 *
 * @returns string | undefined
 */
const validateWeddingDateNotBooked = (
  weddingYear: string | undefined,
  weddingMonth: string | undefined,
  weddingDay: string | number | undefined,
  bookedDates: string[],
  flexible: boolean
): string | undefined => {
  const [weddingDate] = formatWeddingDates(weddingYear, weddingMonth, weddingDay);
  if (weddingDate) {
    const formattedDate = formatDateUtc(weddingDate, 'YYYY-MM-DD');
    const isBooked = bookedDates?.includes(formattedDate);

    if (isBooked && !flexible) {
      return 'Wedding date booked.';
    }
  }

  return undefined;
};

/**
 * Common validator for the wedding date fields  It handles validating that
 * the wedding date provided for non-venues is not booked, and that ranked
 * date selections for wedding venues are not booked.
 *
 * If the date is booked, returns a string indicating an error.  No error
 * is indicated with a return value of undefined.
 */
const validateWeddingDateNotBookedFromForm = (
  values: WeddingDateFormFields,
  field?: RankedDateField
): string | undefined => {
  const { bookedDates } = values;
  const source = field ? values[field] : values;
  let errorOrUndefined;
  if (source) {
    const { weddingDay, weddingYear, weddingMonth } = source;
    const flexible = field ? false : values.flexibleOnDate;

    errorOrUndefined = validateWeddingDateNotBooked(
      weddingYear,
      weddingMonth,
      weddingDay,
      bookedDates || [],
      Boolean(flexible)
    );
  }

  return errorOrUndefined;
};

/**
 * Validates that a wedding date, from individual form selections, is a valid date.
 * Undefined indicates no validation error.
 *
 * @returns string | undefined
 */
const validateThreePartWeddingDate = (
  weddingYear: string | undefined,
  weddingMonth: string | undefined,
  weddingDay: number | undefined
): string | undefined => {
  const errorMessage = 'Please enter a valid date.';
  const year = weddingYear ? Number.parseInt(weddingYear, 10) : undefined;
  const month = weddingMonth ? Number.parseInt(weddingMonth, 10) : undefined;
  const isLeapYear = year !== undefined && (year % 4 === 0 || year % 100 === 0);
  if (month && weddingDay) {
    if ([4, 6, 9, 11].includes(month)) {
      if (weddingDay > 30) {
        return errorMessage;
      }
    } else if (month === 2) {
      if (weddingDay > 29 || (!isLeapYear && weddingDay > 28)) {
        return errorMessage;
      }
    }
  }
  return undefined;
};

/**
 * Validates that a wedding date, from individual form selections, is not
 * in the future.  Validates both full wedding dates and wedding dates that
 * are just year/month.  If the wedding date is in the past a string is returned
 * indicating an error.  Undefined indicates no validation error.
 *
 * @returns string | undefined
 */
const validateWeddingDateInFuture = (
  weddingYear: string | undefined,
  weddingMonth: string | undefined,
  weddingDay: string | number | undefined
): string | undefined => {
  const [weddingDate, weddingYearMonthDate] = formatWeddingDates(
    weddingYear,
    weddingMonth,
    weddingDay
  );
  const isAfterToday = isWeddingDateInTheFuture(weddingDate, weddingYearMonthDate);
  if (weddingDate || weddingYearMonthDate) {
    if (isAfterToday) {
      return undefined;
    }
    return 'Please select a future date.';
  }

  return undefined;
};

/**
 * This is a common validator for the wedding date field.  It handles a validating
 * the wedding date provided for non-venues, and ranked date selection for venues.
 *
 * When the `field` is omitted, we are validating the wedding date for non venues
 * and will allow for just a year+month to be selected.  Otherwise, we are validating
 * the ranked date selections for venues, and you must pick a date.
 *
 * This validates:
 * - A required field not provided (expandedWeddingDateFirstChoice or the non-venue wedding date)
 * - Dates provided are not in the past
 *
 * This does not validate:
 * - Dates are booked
 *
 * This returns a string or undefined.  A string indicates there was a validation failure.
 */
const validateWeddingDateFromForm = (
  values: WeddingDateFormFields,
  field?: RankedDateField
): string | undefined => {
  const source = field ? values[field] : values;

  let errorOrUndefined;
  if (source) {
    const { weddingDay, weddingYear, weddingMonth } = source;

    errorOrUndefined = validateThreePartWeddingDate(weddingYear, weddingMonth, weddingDay);

    if (!errorOrUndefined) {
      errorOrUndefined = validateWeddingDateInFuture(weddingYear, weddingMonth, weddingDay);
    }
  } else if (field === 'expandedWeddingDateFirstChoice' || field === undefined) {
    errorOrUndefined = 'Required';
  }
  return errorOrUndefined;
};

export type WeddingDateFieldError = {
  weddingDate: string | undefined;
  bookedDates: string | undefined;
};

export function validateWeddingDateField(values: WeddingDateFormFields): WeddingDateFieldError;
export function validateWeddingDateField(
  values: WeddingDateFormFields,
  field: RankedDateField
): { [field in RankedDateField]: WeddingDateFieldError };

/**
 * FormValidator for the WeddingDate field.  You probably want to use this
 * combine validators to build a validator for the whole question, not just
 * the date field.
 *
 * @example
 * // Validates the wedding date for non-venues
 * <Form
 *    validate={validateField}
 *   ...
 * />
 *
 * @example
 * // Validates the wedding date for a ranked date field
 * <Form
 *    validate={(values:FormValues) => validateField(values, 'expandedWeddingDateFirstChoice')}
 * ...
 * />
 *
 */
export function validateWeddingDateField(values: WeddingDateFormFields, field?: RankedDateField) {
  const combinedError: WeddingDateFieldError = {
    weddingDate: validateWeddingDateFromForm(values, field),
    bookedDates: validateWeddingDateNotBookedFromForm(values, field),
  };
  if (field) {
    return {
      [field]: combinedError,
    };
  }
  return combinedError;
}
